import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getExpenses, deleteExpense, submitExpense, bulkSubmitExpenses, getCategories, getUsers, resetExpenseToDraft } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "../lib/utils";
import { Checkbox } from "../components/ui/checkbox";
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Send,
  RotateCcw,
  FileText,
  Download,
  Users,
  CalendarIcon,
  X,
  ChevronDown,
  ChevronRight
} from "lucide-react";

const MONTH_NAMES = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

const statusLabels = {
  draft: "Brouillon",
  submitted: "En attente",
  approved: "Approuvé",
  rejected: "Rejeté",
  reimbursed: "Remboursé"
};

export default function Expenses() {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const isManager = hasRole(["admin", "manager", "accountant"]);

  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const monthFilter = filterYear && filterMonth ? `${filterYear}-${filterMonth}` : "";
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    fetchData();
    setSelectedIds(new Set());
  }, [statusFilter, categoryFilter, userFilter, dateFrom, dateTo, filterMonth, filterYear]);

  useEffect(() => {
    if (isManager) {
      getUsers().then(res => setUsers(res.data)).catch(() => {});
    }
  }, [isManager]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (categoryFilter !== "all") params.category_id = categoryFilter;
      if (userFilter !== "all") params.user_id = userFilter;
      if (dateFrom) params.date_from = format(dateFrom, "yyyy-MM-dd");
      if (dateTo) params.date_to = format(dateTo, "yyyy-MM-dd");
      if (monthFilter) params.expense_month = monthFilter;

      const [expensesRes, categoriesRes] = await Promise.all([
        getExpenses(params),
        getCategories()
      ]);

      setExpenses(expensesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!expenseToDelete) return;
    try {
      await deleteExpense(expenseToDelete.id);
      toast.success("Dépense supprimée");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeleteDialogOpen(false);
      setExpenseToDelete(null);
    }
  };

  const handleSubmit = async (expense) => {
    try {
      await submitExpense(expense.id);
      toast.success("Dépense soumise pour validation");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la soumission");
    }
  };

  const handleReset = async (expense) => {
    try {
      await resetExpenseToDraft(expense.id);
      toast.success("Dépense remise en brouillon");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la remise en brouillon");
    }
  };

  const formatCurrency = (amount, currency = "EUR") => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(amount);
  };

  const getStatusClass = (status) => {
    return `status-badge status-${status}`;
  };

  useEffect(() => {
    setSelectedIds(new Set());
  }, [searchTerm]);

  const filteredExpenses = expenses.filter(expense => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      expense.description.toLowerCase().includes(term) ||
      expense.category_name?.toLowerCase().includes(term) ||
      expense.project?.toLowerCase().includes(term)
    );
  });

  const allFilteredSelected = filteredExpenses.length > 0 && filteredExpenses.every(e => selectedIds.has(e.id));
  const someFilteredSelected = filteredExpenses.some(e => selectedIds.has(e.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredExpenses.forEach(e => next.delete(e.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredExpenses.forEach(e => next.add(e.id));
        return next;
      });
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const [expandedYears, setExpandedYears] = useState(new Set());
  const [expandedMonths, setExpandedMonths] = useState(new Set());

  const toggleYear = (year) => {
    setExpandedYears(prev => {
      const next = new Set(prev);
      next.has(year) ? next.delete(year) : next.add(year);
      return next;
    });
  };

  const toggleMonth = (monthKey) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      next.has(monthKey) ? next.delete(monthKey) : next.add(monthKey);
      return next;
    });
  };

  const groupedExpenses = useMemo(() => {
    const groups = {};
    filteredExpenses.forEach(expense => {
      const ref = expense.expense_month || expense.date.slice(0, 7);
      const [year, month] = ref.split("-");
      if (!groups[year]) groups[year] = {};
      if (!groups[year][month]) groups[year][month] = [];
      groups[year][month].push(expense);
    });
    return groups;
  }, [filteredExpenses]);

  const sortedYears = useMemo(() =>
    Object.keys(groupedExpenses).sort((a, b) => b - a),
    [groupedExpenses]
  );

  const selectedExpenses = filteredExpenses.filter(e => selectedIds.has(e.id));
  const submittableSelected = selectedExpenses.filter(e => e.status === "draft" || e.status === "rejected");

  const handleBulkSubmit = async () => {
    if (submittableSelected.length === 0) return;
    try {
      const res = await bulkSubmitExpenses(submittableSelected.map(e => e.id));
      const { processed, errors } = res.data;
      if (processed > 0) toast.success(`${processed} dépense${processed > 1 ? "s" : ""} soumise${processed > 1 ? "s" : ""} pour validation`);
      if (errors > 0) toast.error(`${errors} dépense${errors > 1 ? "s" : ""} non soumise${errors > 1 ? "s" : ""}`);
      setSelectedIds(new Set());
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la soumission");
    }
  };

  const selectionTotals = selectedExpenses.reduce((acc, e) => {
    acc[e.currency] = (acc[e.currency] || 0) + e.amount;
    return acc;
  }, {});

  return (
    <div className="space-y-6 animate-fade-in" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Mes dépenses</h1>
          <p className="page-description">Gérez vos notes de frais</p>
        </div>
        <Link to="/expenses/new">
          <Button className="gap-2" data-testid="new-expense-btn">
            <Plus className="h-4 w-4" />
            Nouvelle dépense
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          {/* Ligne 1 : recherche + statut + catégorie + collaborateur */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="draft">Brouillon</SelectItem>
                <SelectItem value="submitted">En attente</SelectItem>
                <SelectItem value="approved">Approuvé</SelectItem>
                <SelectItem value="rejected">Rejeté</SelectItem>
                <SelectItem value="reimbursed">Remboursé</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="category-filter">
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {isManager && users.length > 0 && (
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger className="w-full sm:w-[200px]" data-testid="user-filter">
                  <Users className="h-4 w-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Collaborateur" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les collaborateurs</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.first_name} {u.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Ligne 2 : mois de note de frais + période */}
          <div className="flex flex-wrap gap-3 items-center">
            <Label className="text-sm text-muted-foreground shrink-0">Mois NDF :</Label>
            <Select value={filterMonth} onValueChange={setFilterMonth}>
              <SelectTrigger className="h-9 w-36" data-testid="month-filter-month">
                <SelectValue placeholder="Mois" />
              </SelectTrigger>
              <SelectContent>
                {["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"].map((nom, i) => (
                  <SelectItem key={i+1} value={String(i+1).padStart(2,"0")}>{nom}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="h-9 w-24" data-testid="month-filter-year">
                <SelectValue placeholder="Année" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({length: 5}, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(filterMonth || filterYear) && (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground h-8 px-2"
                onClick={() => { setFilterMonth(""); setFilterYear(""); }}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            <span className="w-px h-4 bg-border" />
            <Label className="text-sm text-muted-foreground shrink-0">Période :</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn("justify-start text-left font-normal", !dateFrom && "text-muted-foreground")}
                  data-testid="date-from"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateFrom ? format(dateFrom, "d MMM yyyy", { locale: fr }) : "Date de début"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} initialFocus />
              </PopoverContent>
            </Popover>

            <span className="text-muted-foreground text-sm">→</span>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn("justify-start text-left font-normal", !dateTo && "text-muted-foreground")}
                  data-testid="date-to"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateTo ? format(dateTo, "d MMM yyyy", { locale: fr }) : "Date de fin"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={dateTo} onSelect={setDateTo} initialFocus />
              </PopoverContent>
            </Popover>

            {(dateFrom || dateTo) && (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground h-8 px-2"
                onClick={() => { setDateFrom(null); setDateTo(null); }}
                data-testid="clear-dates"
              >
                <X className="h-4 w-4 mr-1" />
                Effacer
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filteredExpenses.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox
                      checked={allFilteredSelected}
                      data-state={someFilteredSelected && !allFilteredSelected ? "indeterminate" : undefined}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Tout sélectionner"
                    />
                  </TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  {isManager && <TableHead>Collaborateur</TableHead>}
                  <TableHead>Catégorie</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedYears.map(year => {
                  const yearExpanded = expandedYears.has(year);
                  const sortedMonths = Object.keys(groupedExpenses[year]).sort((a, b) => b - a);
                  const colSpan = isManager ? 8 : 7;
                  return (
                    <>
                      {/* En-tête année */}
                      <TableRow key={`year-${year}`} className="bg-muted/40 hover:bg-muted/40 border-t-2">
                        <TableCell colSpan={colSpan} className="py-2">
                          <button
                            className="flex items-center gap-2 font-bold text-base w-full text-left"
                            onClick={() => toggleYear(year)}
                          >
                            {yearExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            {year}
                          </button>
                        </TableCell>
                      </TableRow>

                      {yearExpanded && sortedMonths.map(month => {
                        const monthKey = `${year}-${month}`;
                        const monthExpanded = expandedMonths.has(monthKey);
                        const monthExpenses = groupedExpenses[year][month];
                        const monthTotals = monthExpenses.reduce((acc, e) => {
                          acc[e.currency] = (acc[e.currency] || 0) + e.amount;
                          return acc;
                        }, {});

                        return (
                          <>
                            {/* En-tête mois */}
                            <TableRow key={`month-${monthKey}`} className="bg-muted/20 hover:bg-muted/20">
                              <TableCell colSpan={colSpan} className="py-1.5 pl-8">
                                <button
                                  className="flex items-center gap-2 text-sm font-semibold w-full text-left"
                                  onClick={() => toggleMonth(monthKey)}
                                >
                                  {monthExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                  {MONTH_NAMES[parseInt(month) - 1]} {year}
                                  <span className="text-muted-foreground font-normal">
                                    — {monthExpenses.length} dépense{monthExpenses.length > 1 ? "s" : ""}
                                  </span>
                                  <span className="ml-auto font-medium tabular-nums">
                                    {Object.entries(monthTotals).map(([currency, amount], i) => (
                                      <span key={currency}>
                                        {i > 0 && " + "}
                                        {formatCurrency(amount, currency)}
                                      </span>
                                    ))}
                                  </span>
                                </button>
                              </TableCell>
                            </TableRow>

                            {/* Lignes dépenses */}
                            {monthExpanded && monthExpenses.map((expense) => (
                              <TableRow key={expense.id} data-testid={`expense-row-${expense.id}`} data-selected={selectedIds.has(expense.id)}>
                                <TableCell>
                                  <Checkbox
                                    checked={selectedIds.has(expense.id)}
                                    onCheckedChange={() => toggleSelect(expense.id)}
                                    aria-label="Sélectionner"
                                  />
                                </TableCell>
                                <TableCell className="font-medium">{expense.date}</TableCell>
                                <TableCell>
                                  <div>
                                    <p className="font-medium">{expense.description}</p>
                                    {expense.project && (
                                      <p className="text-xs text-muted-foreground">{expense.project}</p>
                                    )}
                                  </div>
                                </TableCell>
                                {isManager && (
                                  <TableCell className="text-sm text-muted-foreground">{expense.user_name}</TableCell>
                                )}
                                <TableCell>{expense.category_name}</TableCell>
                                <TableCell className="text-right tabular-nums font-medium">
                                  {formatCurrency(expense.amount, expense.currency)}
                                </TableCell>
                                <TableCell>
                                  <span className={getStatusClass(expense.status)}>
                                    {statusLabels[expense.status]}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon" data-testid={`expense-actions-${expense.id}`}>
                                        <MoreHorizontal className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => navigate(`/expenses/${expense.id}`)}>
                                        <Eye className="h-4 w-4 mr-2" />
                                        Voir
                                      </DropdownMenuItem>
                                      {(expense.status === "draft" || expense.status === "rejected") && (
                                        <>
                                          <DropdownMenuItem onClick={() => navigate(`/expenses/${expense.id}?edit=true`)}>
                                            <Edit className="h-4 w-4 mr-2" />
                                            Modifier
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => handleSubmit(expense)}>
                                            <Send className="h-4 w-4 mr-2" />
                                            Soumettre
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            className="text-destructive"
                                            onClick={() => {
                                              setExpenseToDelete(expense);
                                              setDeleteDialogOpen(true);
                                            }}
                                          >
                                            <Trash2 className="h-4 w-4 mr-2" />
                                            Supprimer
                                          </DropdownMenuItem>
                                        </>
                                      )}
                                      {expense.status !== "draft" && (expense.user_id === user?.id || isManager) && (
                                        <DropdownMenuItem onClick={() => handleReset(expense)}>
                                          <RotateCcw className="h-4 w-4 mr-2" />
                                          Remettre en brouillon
                                        </DropdownMenuItem>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            ))}
                          </>
                        );
                      })}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">Aucune dépense trouvée</p>
              <Link to="/expenses/new">
                <Button data-testid="empty-new-expense-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Créer une dépense
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Barre de sélection */}
      {selectedExpenses.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 bg-background border shadow-lg rounded-xl px-6 py-3 text-sm">
          <span className="text-muted-foreground">
            {selectedExpenses.length} dépense{selectedExpenses.length > 1 ? "s" : ""} sélectionnée{selectedExpenses.length > 1 ? "s" : ""}
          </span>
          <span className="w-px h-4 bg-border" />
          <span className="font-semibold">
            Total :&nbsp;
            {Object.entries(selectionTotals).map(([currency, amount], i) => (
              <span key={currency}>
                {i > 0 && " + "}
                {formatCurrency(amount, currency)}
              </span>
            ))}
          </span>
          {submittableSelected.length > 0 && (
            <>
              <span className="w-px h-4 bg-border" />
              <Button size="sm" className="h-7 gap-1.5" onClick={handleBulkSubmit}>
                <Send className="h-3.5 w-3.5" />
                Soumettre ({submittableSelected.length})
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-muted-foreground hover:text-foreground"
            onClick={() => setSelectedIds(new Set())}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer cette dépense ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
